import { Link } from "react-router-dom";
import { ArrowRight, Inbox, Play } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { StatCardRow } from "@/components/data-display/stat-card";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useLoadedPermission } from "@/hooks/use-permission";

/**
 * Reconciliation overview (/reconciliation) — WIP layout port of the designer's
 * `features/oversight/reconciliation.tsx` (`ReconciliationOverview`).
 *
 * `alloy-reconflow-service` is a README, not a module: no pom.xml, no src/, no
 * migrations (context/page_pipeline/BACKEND_GAPS.md rows 20-23). So this is the
 * designer's structure — header, stat row, run panels, source freshness matrix —
 * with every value rendered as the shared unknown marker and every panel body a
 * designed empty state. Nothing on this page is fabricated: no run id, no
 * counts, no source list, no timestamps. The controls that need the missing
 * backend stay disabled and carry WIP_ACTION_HINT. Run is also hidden unless the loaded
 * effective-access contract grants its supported command permission; the link points
 * at a route that exists in App.tsx.
 */

/**
 * A value the backend does not carry. `—` rather than a zero or a blank: a zero
 * is a claim about a measurement, and a blank reads as "nothing" (this is the
 * same marker `features/oversight/reconciliation-run-history.tsx` uses for an
 * unknown value).
 */
const UNKNOWN = "—";

/** The four stat labels the designer's overview shows. Labels only — never the fixture values. */
const STAT_LABELS = ["Matched Transactions", "Unmatched", "Breaks", "Sources Fresh"];

/** Id of the hidden node carrying WIP_ACTION_HINT for every disabled control on the page. */
const WIP_ACTIONS_ID = "reconciliation-wip-actions";

/** The designer's panel chrome: bordered section with a title bar. */
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-surface">
      <header className="border-b border-line px-4 py-2">
        <h3 className="text-sm font-bold text-ink">{title}</h3>
      </header>
      {children}
    </section>
  );
}

function PanelEmpty({ title, description }: { title: string; description: string }) {
  return (
    <EmptyState
      title={title}
      description={description}
      icon={<Inbox aria-hidden className="size-5 text-muted" />}
      className="py-8"
    />
  );
}

export default function ReconciliationPage() {
  const { allowed: canRun } = useLoadedPermission("reconciliation:run");
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Reconciliation</h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted">
            Continuously match platform ledger entries against custody providers and chain sources.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canRun && (
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled
              title={WIP_ACTION_HINT}
              aria-describedby={WIP_ACTIONS_ID}
            >
              <Play aria-hidden className="size-4" /> Run reconciliation
            </Button>
          )}
          <Link to="/reconciliation/exceptions" className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
            View Exceptions <ArrowRight aria-hidden className="size-4" />
          </Link>
        </div>
      </div>
      {/* One plain sentence, and the reason every disabled control on this page gives. */}
      <span id={WIP_ACTIONS_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>
      <p className="mb-5 text-sm text-muted">
        Reconciliation has no backend in this environment yet, so nothing below reflects a real run, exception or
        source.
      </p>

      <StatCardRow stats={STAT_LABELS.map((label) => ({ label, value: UNKNOWN }))} />

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Panel title="Current Run">
          <PanelEmpty
            title="No run to report"
            description="No reconciliation service runs in this environment, so there is no run in progress and no progress to measure."
          />
        </Panel>
        <Panel title="Last Completed Run">
          <PanelEmpty
            title="No completed run to report"
            description="No reconciliation service runs in this environment, so no run outcome, duration or break count exists to show."
          />
        </Panel>
      </div>

      <h2 className="mb-3 text-[15px] font-bold text-ink">Source Freshness Matrix</h2>
      <div className="rounded-lg border border-line bg-surface">
        <PanelEmpty
          title="No sources to report"
          description="No custody provider or chain source is wired into reconciliation in this environment, so no freshness reading can be shown."
        />
      </div>
    </div>
  );
}
